/**
 * PrintDoc layout in characters (pure): what a receipt printer's normal font puts on
 * each line. The ESC/POS renderer (M4) and the dev HTML bridge both start here, so a
 * document wraps the same way on paper and on screen.
 *
 * Widths: PAPER_CHARS[paperMm] characters per line; a block of width 2 holds half as
 * many. Text wraps at word boundaries (a word longer than the line is cut). A row
 * shares the line between its cells by `flex`, one space between cells; a cell that
 * does not fit wraps inside its column and the row grows.
 */
import { PAPER_CHARS, type PrintAlign, type PrintBlock, type PrintDoc } from '@qrkey/contracts';

export interface LaidLine {
  readonly text: string;
  readonly bold: boolean;
  readonly underline: boolean;
  readonly invert: boolean;
  readonly width: 1 | 2;
  readonly height: 1 | 2;
}

export type LaidBlock =
  | { readonly kind: 'lines'; readonly lines: readonly LaidLine[] }
  | {
      readonly kind: 'qr';
      readonly data: string;
      readonly size: number;
      readonly align: PrintAlign;
    }
  | {
      readonly kind: 'barcode';
      readonly data: string;
      readonly format: 'code128' | 'ean13';
      readonly align: PrintAlign;
    }
  | { readonly kind: 'logo'; readonly ref: string; readonly align: PrintAlign }
  | { readonly kind: 'cut'; readonly partial: boolean };

/** Characters per line for this document. */
export function lineChars(doc: Pick<PrintDoc, 'paperMm'>): number {
  return PAPER_CHARS[doc.paperMm];
}

/** Word-wrap `text` to `width` characters (newlines kept). */
export function wrap(text: string, width: number): string[] {
  const w = Math.max(1, width);
  const out: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter((x) => x !== '')) {
      let rest = word;
      while (rest.length > w) {
        if (line !== '') {
          out.push(line);
          line = '';
        }
        out.push(rest.slice(0, w));
        rest = rest.slice(w);
      }
      if (rest === '') continue;
      if (line === '') line = rest;
      else if (line.length + 1 + rest.length <= w) line = `${line} ${rest}`;
      else {
        out.push(line);
        line = rest;
      }
    }
    out.push(line);
  }
  return out;
}

export function alignText(text: string, width: number, align: PrintAlign = 'left'): string {
  const t = text.length > width ? text.slice(0, width) : text;
  const gap = width - t.length;
  if (align === 'right') return ' '.repeat(gap) + t;
  if (align === 'center') {
    const left = Math.floor(gap / 2);
    return ' '.repeat(left) + t + ' '.repeat(gap - left);
  }
  return t + ' '.repeat(gap);
}

/** Split `total` characters between cells by flex (each at least 1), gaps of one space. */
export function columnWidths(flexes: readonly number[], total: number): number[] {
  const n = flexes.length;
  const usable = Math.max(n, total - (n - 1));
  const sum = flexes.reduce((a, b) => a + b, 0);
  const widths = flexes.map((f) => Math.max(1, Math.floor((usable * f) / sum)));
  let spare = usable - widths.reduce((a, b) => a + b, 0);
  for (let i = 0; spare > 0; i = (i + 1) % n, spare -= 1) widths[i] = (widths[i] ?? 1) + 1;
  return widths;
}

function style(b: {
  bold?: boolean | undefined;
  underline?: boolean | undefined;
  invert?: boolean | undefined;
  width?: 1 | 2 | undefined;
  height?: 1 | 2 | undefined;
}) {
  return {
    bold: b.bold ?? false,
    underline: b.underline ?? false,
    invert: b.invert ?? false,
    width: b.width ?? 1,
    height: b.height ?? 1,
  } as const;
}

function layoutBlock(block: PrintBlock, chars: number): LaidBlock {
  switch (block.t) {
    case 'text': {
      const s = style(block);
      const width = Math.floor(chars / s.width);
      const lines = wrap(block.text, width).map((t) => ({
        ...s,
        text: alignText(t, width, block.align),
      }));
      return { kind: 'lines', lines };
    }
    case 'row': {
      const s = style(block);
      const width = Math.floor(chars / s.width);
      const widths = columnWidths(
        block.cells.map((c) => c.flex ?? 1),
        width,
      );
      const wrapped = block.cells.map((c, i) => wrap(c.text, widths[i] ?? 1));
      const height = Math.max(...wrapped.map((w) => w.length));
      const lines: LaidLine[] = [];
      for (let r = 0; r < height; r += 1) {
        const text = block.cells
          .map((c, i) => alignText(wrapped[i]?.[r] ?? '', widths[i] ?? 1, c.align))
          .join(' ');
        const bold = s.bold || block.cells.some((c) => c.bold === true);
        lines.push({ ...s, bold, text });
      }
      return { kind: 'lines', lines };
    }
    case 'rule':
      return { kind: 'lines', lines: [{ ...style({}), text: (block.char ?? '-').repeat(chars) }] };
    case 'feed':
      return {
        kind: 'lines',
        lines: Array.from({ length: block.lines }, () => ({ ...style({}), text: '' })),
      };
    case 'qr':
      return {
        kind: 'qr',
        data: block.data,
        size: block.size ?? 6,
        align: block.align ?? 'center',
      };
    case 'barcode':
      return {
        kind: 'barcode',
        data: block.data,
        format: block.format,
        align: block.align ?? 'center',
      };
    case 'logo':
      return { kind: 'logo', ref: block.ref, align: block.align ?? 'center' };
    case 'cut':
      return { kind: 'cut', partial: block.partial ?? false };
  }
}

/** Lay a document out (one copy). */
export function layoutDoc(doc: PrintDoc): LaidBlock[] {
  const chars = lineChars(doc);
  return doc.blocks.map((b) => layoutBlock(b, chars));
}

/**
 * Plain text of one copy, for logs, tests and the browser's fallback: lines padded
 * to the paper width; a QR as `[QR: data]`, a barcode as `[BARCODE: data]`, a cut as
 * a line of `~`. Double-width lines are not stretched.
 */
export function renderText(doc: PrintDoc): string {
  const chars = lineChars(doc);
  const out: string[] = [];
  for (const b of layoutDoc(doc)) {
    switch (b.kind) {
      case 'lines':
        out.push(...b.lines.map((l) => l.text.trimEnd()));
        break;
      case 'qr':
        out.push(alignText(`[QR: ${b.data}]`, chars, b.align).trimEnd());
        break;
      case 'barcode':
        out.push(alignText(`[BARCODE: ${b.data}]`, chars, b.align).trimEnd());
        break;
      case 'logo':
        out.push(alignText(`[LOGO: ${b.ref}]`, chars, b.align).trimEnd());
        break;
      case 'cut':
        out.push('~'.repeat(chars));
        break;
    }
  }
  return out.join('\n');
}
